import { ReactNode } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import {
  AcademicCapIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

import PendencyRow from '@/components/student-dashboard/PendencyRow';
import { ExamPendency } from '@/data/hook/student-dashboard/useStudentExamPendency';
import useVisiblePendencies, {
  Pendency,
  PendencyType,
} from '@/data/hook/student-dashboard/useVisiblePendencies';

const DUE_DATE_FORMAT = 'DD/MM/YY';

const ICONS: Record<PendencyType, ReactNode> = {
  [PendencyType.SCHEDULED_EXAM]: <CalendarIcon className="size-5" />,
  [PendencyType.HOMEWORK]: <DocumentTextIcon className="size-5" />,
  [PendencyType.NEXT_LESSON]: <AcademicCapIcon className="size-5" />,
};

const TITLE_KEYS: Record<PendencyType, string> = {
  [PendencyType.SCHEDULED_EXAM]: 'scheduledExam',
  [PendencyType.HOMEWORK]: 'pendingHomeworks',
  [PendencyType.NEXT_LESSON]: 'nextLesson',
};

const DESCRIPTION_KEYS: Partial<Record<PendencyType, string>> = {
  [PendencyType.SCHEDULED_EXAM]: 'doUntil',
  [PendencyType.NEXT_LESSON]: 'happensOn',
};

type PendencyListProps = {
  exam: ExamPendency;
  homeworkCount: number;
  nextLessonDate?: string;
  limit: number;
  className?: string;
};

export default function PendencyList({
  exam,
  homeworkCount,
  nextLessonDate,
  limit,
  className,
}: PendencyListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.continuity.pendencies',
  });

  const pendencies = useVisiblePendencies({
    exam,
    homeworkCount,
    nextLessonDate,
    limit,
  });

  const getDescription = ({ type, date }: Pendency) => {
    const key = DESCRIPTION_KEYS[type];
    const parsedDate = moment(date ?? null);
    if (!key || !parsedDate.isValid()) return undefined;
    return t(key, { date: parsedDate.format(DUE_DATE_FORMAT) });
  };

  return (
    <ul className={twMerge('flex flex-col gap-4', className)}>
      {pendencies.map(pendency => (
        <PendencyRow
          key={pendency.type}
          icon={ICONS[pendency.type]}
          title={t(TITLE_KEYS[pendency.type])}
          description={getDescription(pendency)}
          count={pendency.count}
        />
      ))}
    </ul>
  );
}
